import type { Vista } from '../map/mapa';
import type { CapaBase } from './estado';
import type { ModoVuelo } from '../juego/dron';

/**
 * Direcciones de la aplicacion, que son tambien los enlaces para compartir. La ruta dice que se abre
 * y los parametros, como:
 *
 *   /?m=z/lat/lon/rumbo/incl&base=orto&cat=0.5&3d=1&hist=PNOA2010    mapa
 *   /p/{RC}[?m=...]                                                  parcela seleccionada (sin m, encuadrada)
 *   /p/{RC}/3d?a=...&d=...&c=...&capas=...&color=...&punto=...&orto=...&edif=...
 *                                                                    modelo LiDAR 3D de la parcela
 *   /p/{RC}/paseo?a=...&d=...&c=...                                  modo de juego a pie
 *   /p/{RC}/dron?a=...&d=...&c=...&vuelo=angle&cam=fpv               modo de juego en el dron
 *   /p/{RC}/buggy?a=...&d=...&c=...&cam=fpv                          modo de juego al volante del buggy 4x4
 *
 * `a` es el area ya calculada en el servidor (el enlace abre el modelo sin calcular nada); si falta o
 * ha caducado, el modelo se calcula de nuevo con la referencia y el desfase `d` (0-150 m).
 * `c=x,y,z,rumbo,cabeceo[,dist]` va en el marco del modelo: metros desde su origen (el centro de la
 * caja de la parcela redondeado al metro y, en Z, la cota mas baja del terreno dentro de la parcela:
 * el mismo «origen local» de los DXF), el rumbo en grados desde el norte en sentido horario y el
 * cabeceo en grados (negativo, hacia abajo). En la vista 3D x,y,z es la camara y dist la distancia al
 * punto alrededor del que gira; en el paseo, los pies; en el dron, su centro, y el cabeceo es la
 * inclinacion de su camara FPV; en el buggy, el punto del suelo donde se aparca (x, y) y su rumbo.
 * `cam` es la camara del dron o del buggy: `seg` detras, `fpv` la FPV del dron o la del conductor del buggy.
 *
 * Se siguen leyendo los enlaces de antes, con todo en el hash (#map=...&rc=...).
 */
export type Pantalla = 'mapa' | 'parcela' | '3d' | 'paseo' | 'dron' | 'buggy';
export type PantallaEscena = '3d' | 'paseo' | 'dron' | 'buggy';
export type ColorNube = 'rgb' | 'clase' | 'altura';
export type ModoEdificios = 'solidos' | 'aristas' | 'ambos';
export type CamaraVehiculo = 'seguimiento' | 'fpv';

export interface EstadoMapaUrl {
  vista: Vista | null;
  base: CapaBase | null;
  /** Opacidad de la cartografia catastral (0 = apagada); null = la de siempre. */
  catastro: number | null;
  modo3d: boolean;
  historico: string | null;
}

/** Camara, jugador o dron en el marco del modelo (ver arriba). */
export interface CamaraUrl {
  x: number;
  y: number;
  z: number;
  rumbo: number;
  cabeceo: number;
  dist: number | null;
}

/** Estado de la escena del modelo; null = lo que haria la escena por su cuenta. */
export interface EscenaUrl {
  area: string | null;
  desfase: number | null;
  camara: CamaraUrl | null;
  /** Capas visibles (ids de las casillas: terreno, cubiertas, techos, tejados, muros, arboles, arbustos, hierba, curvas, huellas, nube). */
  capas: string[] | null;
  color: ColorNube | null;
  punto: number | null;
  orto: boolean | null;
  edificios: ModoEdificios | null;
  vuelo: ModoVuelo | null;
  camVehiculo: CamaraVehiculo | null;
}

export interface Ruta {
  pantalla: Pantalla;
  rc: string | null;
  mapa: EstadoMapaUrl;
  escena: EscenaUrl;
}

export const MAPA_VACIO: Readonly<EstadoMapaUrl> = { vista: null, base: null, catastro: null, modo3d: false, historico: null };
export const ESCENA_VACIA: Readonly<EscenaUrl> = {
  area: null, desfase: null, camara: null, capas: null, color: null, punto: null, orto: null, edificios: null, vuelo: null, camVehiculo: null,
};
/** Tope del desfase (el margen maximo que calcula el servidor). */
export const DESFASE_MAX_URL = 150;

const BASES: CapaBase[] = ['ign', 'orto', 'mtn', 'lidar'];
const COLORES: ColorNube[] = ['rgb', 'clase', 'altura'];
const EDIFICIOS: ModoEdificios[] = ['solidos', 'aristas', 'ambos'];
const VUELOS: ModoVuelo[] = ['acro', 'horizon', 'angle'];
const RC = /^[A-Z0-9]{14}$/i;
const AREA = /^[0-9a-f]{16}$/;

export function esEscena(p: Pantalla): p is PantallaEscena {
  return p === '3d' || p === 'paseo' || p === 'dron' || p === 'buggy';
}

function numero(v: string | null): number | null {
  if (v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function limitar(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/** Lee la direccion de la pagina (`location.pathname`, `location.search`, `location.hash`). */
export function leerRuta(pathname: string, search = '', hash = ''): Ruta {
  const r: Ruta = { pantalla: 'mapa', rc: null, mapa: { ...MAPA_VACIO }, escena: { ...ESCENA_VACIA } };
  let seg: string[] = [];
  try { seg = pathname.split('/').filter(Boolean).map(decodeURIComponent); } catch { /* ruta mal escrita: el mapa */ }
  if (seg[0] === 'p' && seg[1] && RC.test(seg[1])) {
    r.rc = seg[1].toUpperCase();
    r.pantalla = seg[2] === '3d' || seg[2] === 'paseo' || seg[2] === 'dron' || seg[2] === 'buggy' ? seg[2] : 'parcela';
  }
  const q = new URLSearchParams(search.replace(/^\?/, ''));
  // Enlaces de antes: todo en el hash. Lo nuevo manda.
  const viejo = new URLSearchParams(hash.replace(/^#/, ''));
  const p = (k: string) => q.get(k) ?? viejo.get(k);

  const m = p('m') ?? p('map');
  if (m) {
    const [z, lat, lon, b, pt] = m.split('/').map(numero);
    if (z !== null && lat !== null && lon !== null && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
      r.mapa.vista = { zoom: limitar(z, 0, 24), centro: [lon, lat], bearing: b ?? 0, pitch: limitar(pt ?? 0, 0, 85) };
    }
  }
  const base = p('base') as CapaBase | null;
  if (base && BASES.includes(base)) r.mapa.base = base;
  const cat = numero(p('cat'));
  if (cat !== null) r.mapa.catastro = limitar(cat, 0, 1);
  r.mapa.modo3d = p('3d') === '1';
  const hist = p('hist');
  if (hist && /^PNOA\d{4}$/.test(hist)) r.mapa.historico = hist;
  if (!r.rc) {
    const rc = p('rc');
    if (rc && RC.test(rc)) { r.rc = rc.toUpperCase(); r.pantalla = 'parcela'; }
  }

  const e = r.escena;
  const a = q.get('a');
  if (a && AREA.test(a)) e.area = a;
  const d = numero(q.get('d'));
  if (d !== null) e.desfase = Math.round(limitar(d, 0, DESFASE_MAX_URL));
  const c = q.get('c')?.split(',').map(numero);
  if (c && c.length >= 3 && c.slice(0, 3).every((v) => v !== null)) {
    const dist = c[5] ?? null;
    e.camara = {
      x: c[0]!, y: c[1]!, z: c[2]!,
      rumbo: ((c[3] ?? 0) % 360 + 360) % 360,
      cabeceo: limitar(c[4] ?? 0, -90, 90),
      dist: dist !== null && dist > 0 ? dist : null,
    };
  }
  const capas = q.get('capas');
  if (capas !== null) e.capas = capas.split(',').map((s) => s.trim().toLowerCase()).filter((s) => /^[a-z]+$/.test(s));
  const color = q.get('color') as ColorNube | null;
  if (color && COLORES.includes(color)) e.color = color;
  const punto = numero(q.get('punto'));
  if (punto !== null) e.punto = limitar(punto, 1, 8);
  const orto = q.get('orto');
  if (orto === '1' || orto === '0') e.orto = orto === '1';
  const edif = q.get('edif') as ModoEdificios | null;
  if (edif && EDIFICIOS.includes(edif)) e.edificios = edif;
  const vuelo = q.get('vuelo') as ModoVuelo | null;
  if (vuelo && VUELOS.includes(vuelo)) e.vuelo = vuelo;
  const cam = q.get('cam');
  if (cam === 'fpv' || cam === 'seg') e.camVehiculo = cam === 'fpv' ? 'fpv' : 'seguimiento';
  return r;
}

/** Numero con `decimales` como mucho, sin ceros de sobra ni «-0». */
function fmt(v: number, decimales: number): string {
  const f = 10 ** decimales;
  const n = Math.round(v * f) / f;
  return String(Object.is(n, -0) ? 0 : n);
}

/** Direccion (ruta y parametros, sin origen) de un estado. */
export function rutaAUrl(r: Ruta): string {
  const escena = esEscena(r.pantalla) && r.rc !== null;
  let ruta = '/';
  if (r.rc && r.pantalla !== 'mapa') ruta = `/p/${r.rc}${escena ? `/${r.pantalla}` : ''}`;
  const q: string[] = [];
  // Sin codificar comas ni barras: los parametros solo llevan numeros y palabras.
  const poner = (k: string, v: string) => q.push(`${k}=${encodeURIComponent(v).replace(/%2C/gi, ',').replace(/%2F/gi, '/')}`);
  if (!escena) {
    const m = r.mapa;
    const v = m.vista;
    if (v) poner('m', `${fmt(v.zoom, 2)}/${fmt(v.centro[1], 6)}/${fmt(v.centro[0], 6)}/${fmt(v.bearing, 0)}/${fmt(v.pitch, 0)}`);
    if (m.base) poner('base', m.base);
    if (m.catastro !== null) poner('cat', fmt(m.catastro, 2));
    if (m.modo3d) poner('3d', '1');
    if (m.historico) poner('hist', m.historico);
  } else {
    const e = r.escena;
    if (e.area) poner('a', e.area);
    if (e.desfase !== null) poner('d', fmt(e.desfase, 0));
    const c = e.camara;
    if (c) {
      const partes = [fmt(c.x, 1), fmt(c.y, 1), fmt(c.z, 1), fmt(c.rumbo, 1), fmt(c.cabeceo, 1)];
      if (c.dist !== null) partes.push(fmt(c.dist, 1));
      poner('c', partes.join(','));
    }
    if (e.capas) poner('capas', e.capas.join(','));
    if (e.color) poner('color', e.color);
    if (e.punto !== null) poner('punto', fmt(e.punto, 1));
    if (e.orto !== null) poner('orto', e.orto ? '1' : '0');
    if (e.edificios) poner('edif', e.edificios);
    if (r.pantalla === 'dron' && e.vuelo) poner('vuelo', e.vuelo);
    if ((r.pantalla === 'dron' || r.pantalla === 'buggy') && e.camVehiculo) poner('cam', e.camVehiculo === 'fpv' ? 'fpv' : 'seg');
  }
  return q.length ? `${ruta}?${q.join('&')}` : ruta;
}

// ---------- Angulos del marco del modelo (X = este, Y = norte, Z arriba) ----------

const GRADOS = 180 / Math.PI;

/** Rumbo (grados desde el norte, horario) de una direccion en planta. */
export function rumboDe(dx: number, dy: number): number {
  return (Math.atan2(dx, dy) * GRADOS + 360) % 360;
}

/** Rumbo de una guinada de three.js y del juego (radianes desde el este, antihorario). */
export function rumboDeGuinada(yaw: number): number {
  return ((90 - yaw * GRADOS) % 360 + 360) % 360;
}

export function guinadaDeRumbo(rumbo: number): number {
  return (90 - rumbo) / GRADOS;
}

/** Vector unitario hacia donde se mira con ese rumbo y ese cabeceo. */
export function direccionDe(rumbo: number, cabeceo: number): [number, number, number] {
  const r = rumbo / GRADOS, c = cabeceo / GRADOS;
  return [Math.cos(c) * Math.sin(r), Math.cos(c) * Math.cos(r), Math.sin(c)];
}

type P3 = readonly [number, number, number];

/** Camara de orbita (ojo y punto de giro, en el marco del modelo) como parametro `c`. */
export function camaraDeOrbita(ojo: P3, objetivo: P3): CamaraUrl {
  const dx = objetivo[0] - ojo[0], dy = objetivo[1] - ojo[1], dz = objetivo[2] - ojo[2];
  const planta = Math.hypot(dx, dy);
  return {
    x: ojo[0], y: ojo[1], z: ojo[2],
    rumbo: planta > 1e-9 ? rumboDe(dx, dy) : 0,
    cabeceo: Math.atan2(dz, planta) * GRADOS,
    dist: Math.hypot(dx, dy, dz),
  };
}

/** Punto de giro de una camara del enlace: `dist` por delante del ojo (o `distPorDefecto`). */
export function objetivoDeCamara(c: CamaraUrl, distPorDefecto: number): [number, number, number] {
  const [dx, dy, dz] = direccionDe(c.rumbo, c.cabeceo);
  const d = c.dist ?? distPorDefecto;
  return [c.x + dx * d, c.y + dy * d, c.z + dz * d];
}
